/* eslint-disable no-console */
import React, { useEffect } from 'react';
import {
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { get, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import InfoIcon from '@material-ui/icons/Info';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';

import { Box, Button, WrappedAutocomplete, WrappedTextInput } from 'components';
import { createUpdateInvestorKyc, getKycSSN } from 'services';
import { getInvestorDetails } from 'redux-modules/investor/Actions';
import { countryCodeList, errorMessageHandler } from 'common/utils/helpers';
import { RootState } from 'redux-modules/store/rootState';
import { FUNDING_ENTITY_TYPE } from 'common/utils/constants';

import styles from './styles';
import { IndividualFundingSchema } from '../validations';

type Props = {
  handleNext: any;
  handleBack: any;
  spv: string;
  investor: any;
  kycDetail: any;
  profileKyc: any;
  isRollover: boolean;
};

const IndividualFundingForm = ({
  handleNext,
  handleBack,
  spv,
  investor,
  kycDetail,
  profileKyc,
  isRollover = false,
}: Props) => {
  const dispatch = useDispatch();
  const classes = styles();

  const hintText = `If you are a non-US investor, please enter equivalent social security or tax identification number applicable in your country.`;
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [showSSN, setShowSSN] = React.useState<boolean>(false);
  const [isEditModeSSN, setIsEditModeSSN] = React.useState<boolean>(true);

  const { investmentProcess } = useSelector(
    ({ Investor }: RootState) => Investor,
  );
  const { investmentDetail } = investor;
  const investmentData = investmentDetail?.find(
    (investment: any) =>
      investment.spv === spv && investment.kycId === get(kycDetail, 'id'),
  );

  const { handleSubmit, control, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(IndividualFundingSchema),
  });

  const handleClickShowSSN = () => {
    if (!showSSN) {
      getKycSSN(get(kycDetail, 'id'))
        .then((res: any) => {
          setValue('ssn', res);
          setShowSSN(!showSSN);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
        });
    } else {
      setValue('ssn', get(kycDetail, 'ssn'));
      setShowSSN(!showSSN);
    }
  };

  const handleClickEditSSN = () => {
    if (!isEmpty(get(kycDetail, 'id'))) {
      getKycSSN(get(kycDetail, 'id'))
        .then((res: any) => {
          setValue('ssn', res);
          setIsEditModeSSN(!isEditModeSSN);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
        });
    }
  };

  const handleCloseEditSSN = () => {
    setIsEditModeSSN(!isEditModeSSN);
    setValue('ssn', get(kycDetail, 'ssn'));
  };

  const handleMouseDownSSN = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const onSubmit = (data: any): void => {
    setIsLoading(true);
    const payload = {
      ...data,
      phone: `${get(data, 'countryCode')}#${get(data, 'phone')}`,
      spv: get(kycDetail, 'spv') || spv,
      investorId: get(investor, 'id'),
      fundingEntityType: FUNDING_ENTITY_TYPE.INDIVIDUAL,
    };
    if (get(kycDetail, 'id')) payload.id = get(kycDetail, 'id');
    if (isRollover) {
      payload.isRollover = isRollover;
      payload.investementDetailId = get(investmentData, 'id');
    }
    if (!isEditModeSSN) delete payload.ssn;
    createUpdateInvestorKyc(payload)
      .then((res: any) => {
        console.log('res----', res);
        setIsLoading(false);
        dispatch(getInvestorDetails(false));
        setIsEditModeSSN(!isEditModeSSN);
        handleNext();
      })
      .catch((err: any) => {
        console.log('err---', err);
        setIsLoading(false);
        const message: string = errorMessageHandler(err);
        setErrorMessage(message);
      });
  };

  useEffect(() => {
    if (get(kycDetail, 'id')) {
      const phoneSplit = get(kycDetail, 'phone')?.split('#') || [];
      setValue('signatoryLegalName', get(kycDetail, 'signatoryLegalName'));
      setValue(
        'dob',
        isEmpty(get(kycDetail, 'dob')) ? '1990-01-01' : get(kycDetail, 'dob'),
      );
      setValue(
        'countryCode',
        (phoneSplit.length === 2 && phoneSplit[0]) || '+1',
      );
      setValue(
        'phone',
        (phoneSplit.length === 2 ? phoneSplit[1] : phoneSplit[0]) || '',
      );
      setValue('email', get(kycDetail, 'email'));
      setValue('ssn', get(kycDetail, 'ssn'));
      setValue('address1', get(kycDetail, 'address1'));
      setValue('zip', get(kycDetail, 'zip'));
      if (!isEmpty(get(kycDetail, 'ssn'))) setIsEditModeSSN(false);
    } else if (get(profileKyc, 'id')) {
      const phoneSplit = get(profileKyc, 'phone')?.split('#') || [];
      setValue('signatoryLegalName', get(profileKyc, 'signatoryLegalName'));
      setValue(
        'dob',
        isEmpty(get(profileKyc, 'dob')) ? '1990-01-01' : get(profileKyc, 'dob'),
      );
      setValue(
        'countryCode',
        (phoneSplit.length === 2 && phoneSplit[0]) || '+1',
      );
      setValue(
        'phone',
        (phoneSplit.length === 2 ? phoneSplit[1] : phoneSplit[0]) || '',
      );
      setValue('email', get(profileKyc, 'email'));
      setValue('address1', get(profileKyc, 'address1'));
      setValue('zip', get(profileKyc, 'zip'));
    } else {
      setValue('email', get(investor, 'email'));
      setValue('signatoryLegalName', get(investor, 'name'));
      setValue('dob', '1990-01-01');
      setValue('countryCode', '+1');
    }
  }, [kycDetail]);

  return (
    <Box>
      {errorMessage && (
        <Typography variant="body1" className={classes.errorMessage}>
          {errorMessage}
        </Typography>
      )}
      <form
        onSubmit={
          get(investmentProcess, 'docuSignDocumentSigned')
            ? () => handleNext()
            : handleSubmit(onSubmit)
        }
        data-testid="individualFundingForm"
        className={classes.validateForm}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={6}>
            <Typography className={classes.labelText} variant="body1">
              What is the signatory's legal name?
            </Typography>
            <Controller
              name="signatoryLegalName"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Box className={classes.controllerBox}>
                  <WrappedTextInput
                    type="text"
                    placeholder="Name"
                    className={classes.textValInput}
                    InputProps={{ classes: { root: classes.inputRoot } }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelRoot,
                        focused: classes.labelFocused,
                      },
                    }}
                    value={value}
                    onChange={(e: any) => onChange(e.target.value.trimStart())}
                    error={!!error?.message}
                    disabled={get(investmentProcess, 'docuSignDocumentSigned')}
                  />
                  {error?.message && (
                    <span className={classes.errorText}>{error?.message}</span>
                  )}
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography className={classes.labelText} variant="body1">
              Date of Birth
            </Typography>
            <Controller
              name="dob"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Box className={classes.controllerBox}>
                  <TextField
                    id="date-local"
                    type="date"
                    placeholder="DD/MM/YYYY"
                    value={value}
                    className={classes.textValInput}
                    InputProps={{ classes: { root: classes.inputRoot } }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelRoot,
                        focused: classes.labelFocused,
                      },
                    }}
                    error={error?.message ? true : false}
                    onChange={onChange}
                    disabled={get(investmentProcess, 'docuSignDocumentSigned')}
                  />
                  {error?.message && (
                    <span className={classes.errorText}>{error?.message}</span>
                  )}
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography className={classes.labelText} variant="body1">
              Phone
            </Typography>
            <Box className={classes.selectedOpt}>
              <Box className={classes.selectedOptL}>
                <Controller
                  name="countryCode"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Box className={classes.controllerBox}>
                      <WrappedAutocomplete
                        id="field1"
                        disableClearable
                        options={countryCodeList}
                        value={value}
                        getOptionSelected={(option: any, value: any) =>
                          option === value
                        }
                        onChange={(_: any, value: any) => {
                          onChange(value);
                        }}
                        className={classes.textValInput}
                        InputProps={{
                          classes: { root: classes.inputRoot },
                        }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                        error={!!error?.message}
                        disabled={get(
                          investmentProcess,
                          'docuSignDocumentSigned',
                        )}
                      />
                      {error?.message && (
                        <span className={classes.errorTextCountry}>
                          {error?.message}
                        </span>
                      )}
                    </Box>
                  )}
                />
              </Box>
              <Box className={classes.selectedOptR}>
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Box className={classes.controllerBox}>
                      <WrappedTextInput
                        type="text"
                        placeholder="Phone"
                        className={classes.textValInput}
                        InputProps={{ classes: { root: classes.inputRoot } }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                        value={value}
                        onChange={(e: any) =>
                          onChange(e.target.value.trimStart())
                        }
                        error={!!error?.message}
                        disabled={get(
                          investmentProcess,
                          'docuSignDocumentSigned',
                        )}
                      />
                      {error?.message && (
                        <span className={classes.errorText}>
                          {error?.message}
                        </span>
                      )}
                    </Box>
                  )}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography className={classes.labelText} variant="body1">
              Email ID
            </Typography>
            <Controller
              name="email"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Box className={classes.controllerBox}>
                  <WrappedTextInput
                    type="email"
                    placeholder="Email"
                    className={classes.textValInput}
                    InputProps={{ classes: { root: classes.inputRoot } }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelRoot,
                        focused: classes.labelFocused,
                      },
                    }}
                    value={value}
                    onChange={(e: any) => onChange(e.target.value.trimStart())}
                    error={!!error?.message}
                    disabled={get(investmentProcess, 'docuSignDocumentSigned')}
                  />
                  {error?.message && (
                    <span className={classes.errorText}>{error?.message}</span>
                  )}
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography className={classes.labelText} variant="body1">
              Social Security Number (SSN)
            </Typography>
            <Controller
              name="ssn"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Box className={classes.controllerBox}>
                  <WrappedTextInput
                    type="text"
                    placeholder="Social Security Number (SSN)"
                    className={classes.textValInput}
                    InputProps={{
                      classes: { root: classes.inputRoot },
                      endAdornment: (
                        <>
                          {!isEditModeSSN &&
                            get(kycDetail, 'ssn') &&
                            !get(
                              investmentProcess,
                              'docuSignDocumentSigned',
                            ) && (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="edit"
                                  onClick={handleClickEditSSN}
                                  onMouseDown={handleMouseDownSSN}
                                  edge="end"
                                >
                                  <EditIcon />
                                </IconButton>
                              </InputAdornment>
                            )}
                          {isEditModeSSN &&
                            get(kycDetail, 'ssn') &&
                            !get(
                              investmentProcess,
                              'docuSignDocumentSigned',
                            ) && (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="close"
                                  onClick={handleCloseEditSSN}
                                  onMouseDown={handleMouseDownSSN}
                                  edge="end"
                                >
                                  <CloseIcon />
                                </IconButton>
                              </InputAdornment>
                            )}
                          {get(kycDetail, 'ssn') && (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle visibility"
                                onClick={handleClickShowSSN}
                                onMouseDown={handleMouseDownSSN}
                                edge="end"
                                disabled={isEditModeSSN}
                              >
                                {showSSN ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                            </InputAdornment>
                          )}
                        </>
                      ),
                    }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelRoot,
                        focused: classes.labelFocused,
                      },
                    }}
                    value={value}
                    onChange={(e: any) => onChange(e.target.value.trimStart())}
                    error={!!error?.message}
                    disabled={
                      !isEditModeSSN ||
                      get(investmentProcess, 'docuSignDocumentSigned')
                    }
                  />
                  {error?.message && (
                    <span className={classes.errorText}>{error?.message}</span>
                  )}
                  <Box className={classes.toolTipBox}>
                    <Typography className={classes.toolTipText} variant="body1">
                      <Typography
                        variant="caption"
                        className={classes.nonUsTxt}
                      >
                        Non-US Investor? &nbsp;
                      </Typography>{' '}
                      <InfoIcon className={classes.infoIcon} />
                    </Typography>
                    <Typography className={classes.hintTxt}>
                      {hintText}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography className={classes.labelText} variant="body1">
              Address
            </Typography>
            <Controller
              name="address1"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Box className={classes.controllerBox}>
                  <WrappedTextInput
                    type="text"
                    placeholder="Address"
                    className={classes.textValInput}
                    InputProps={{ classes: { root: classes.inputRoot } }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelRoot,
                        focused: classes.labelFocused,
                      },
                    }}
                    value={value}
                    onChange={(e: any) => onChange(e.target.value.trimStart())}
                    error={!!error?.message}
                    disabled={get(investmentProcess, 'docuSignDocumentSigned')}
                  />
                  {error?.message && (
                    <span className={classes.errorText}>{error?.message}</span>
                  )}
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography className={classes.labelText} variant="body1">
              Zip
            </Typography>
            <Controller
              name="zip"
              control={control}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Box className={classes.controllerBox}>
                  <WrappedTextInput
                    type="text"
                    placeholder="Zip"
                    className={classes.textValInput}
                    InputProps={{ classes: { root: classes.inputRoot } }}
                    InputLabelProps={{
                      classes: {
                        root: classes.labelRoot,
                        focused: classes.labelFocused,
                      },
                    }}
                    value={value}
                    onChange={(e: any) => onChange(e.target.value.trimStart())}
                    error={!!error?.message}
                    disabled={get(investmentProcess, 'docuSignDocumentSigned')}
                  />
                  {error?.message && (
                    <span className={classes.errorText}>{error?.message}</span>
                  )}
                </Box>
              )}
            />
          </Grid>
        </Grid>

        <Box className={classes.nextPrevButtonBox}>
          {isRollover && (
            <Button
              variant="outlined"
              className={classes.prevButton}
              name="Previous"
              onClick={() => handleBack()}
            />
          )}
          <Button
            type="submit"
            className={classes.nextButton}
            name="Next"
            isLoading={isLoading}
            disabled={isLoading}
          />
        </Box>
      </form>
    </Box>
  );
};
export default IndividualFundingForm;
